import { z } from "zod";

/**
 * In-app support requests (PLA-48). Shared by the api (input validation, DB
 * CHECK values) and the web/admin apps (form, labels), so the contract stays
 * typed end-to-end.
 */
export const SUPPORT_REQUEST_TYPES = [
	"bug",
	"help",
	"feature_request",
	"feedback",
] as const;
export type SupportRequestType = (typeof SUPPORT_REQUEST_TYPES)[number];

export const SUPPORT_REQUEST_STATUSES = [
	"open",
	"in_progress",
	"resolved",
	"closed",
] as const;
export type SupportRequestStatus = (typeof SUPPORT_REQUEST_STATUSES)[number];

/** Who wrote a message in a support request's thread (PLA-642). */
export const SUPPORT_MESSAGE_AUTHOR_ROLES = ["user", "support"] as const;
export type SupportMessageAuthorRole =
	(typeof SUPPORT_MESSAGE_AUTHOR_ROLES)[number];

/**
 * What the sender sees: the internal triage status stays in admin, users
 * only learn whether their request is still being handled, waiting for them
 * (support replied last) or done.
 */
export const SUPPORT_REQUEST_PUBLIC_STATUSES = [
	"received",
	"replied",
	"done",
] as const;
export type SupportRequestPublicStatus =
	(typeof SUPPORT_REQUEST_PUBLIC_STATUSES)[number];

export const toPublicSupportStatus = (
	status: SupportRequestStatus,
	lastMessageRole: SupportMessageAuthorRole,
): SupportRequestPublicStatus => {
	if (status === "resolved" || status === "closed") {
		return "done";
	}
	return lastMessageRole === "support" ? "replied" : "received";
};

export const SUPPORT_MESSAGE_MAX_LENGTH = 5000;

/**
 * How long an agent can take back their own reply (PLA-642) — for a reply
 * sent to the wrong request or with a broken link.
 */
export const SUPPORT_REPLY_DELETE_WINDOW_MS = 5 * 60 * 1000;

/** Client snapshot captured when the request is sent — helps support reproduce. */
export const supportRequestContextSchema = z.object({
	path: z.string().max(2048),
	search: z.string().max(2048),
	appVersion: z.string().max(64),
	buildCommit: z.string().max(64),
	userAgent: z.string().max(1024),
	viewport: z.object({
		width: z.number().int().nonnegative(),
		height: z.number().int().nonnegative(),
		devicePixelRatio: z.number().positive(),
	}),
	locale: z.string().max(16),
	online: z.boolean(),
	sentryReplayId: z.string().max(64).optional(),
	projectId: z.uuid().optional(),
	drawingId: z.uuid().optional(),
});
export type SupportRequestContext = z.infer<typeof supportRequestContextSchema>;

export const supportRequestTypeSchema = z.enum(SUPPORT_REQUEST_TYPES);

export const supportMessageSchema = z
	.string()
	.trim()
	.min(1)
	.max(SUPPORT_MESSAGE_MAX_LENGTH);

const MB = 1024 * 1024;

/** Files a user may attach to a support request, with per-type size caps. */
export const SUPPORT_ATTACHMENT_MAX_BYTES_BY_TYPE: Readonly<
	Record<string, number>
> = {
	"image/png": 10 * MB,
	"image/jpeg": 10 * MB,
	"image/webp": 10 * MB,
	"image/gif": 10 * MB,
	"image/heic": 10 * MB,
	"application/pdf": 10 * MB,
	"text/plain": 10 * MB,
	"video/mp4": 50 * MB,
	"video/quicktime": 50 * MB,
};

export const SUPPORT_ATTACHMENT_CONTENT_TYPES = Object.keys(
	SUPPORT_ATTACHMENT_MAX_BYTES_BY_TYPE,
);

export const SUPPORT_ATTACHMENT_MAX_COUNT = 5;

/**
 * Size cap for `contentType`, or null when the type is not allowed. Own keys
 * only: a lookup like `"constructor"` must not hit Object.prototype.
 */
export const supportAttachmentMaxBytes = (
	contentType: string,
): number | null =>
	Object.hasOwn(SUPPORT_ATTACHMENT_MAX_BYTES_BY_TYPE, contentType)
		? (SUPPORT_ATTACHMENT_MAX_BYTES_BY_TYPE[contentType] ?? null)
		: null;

export const SUPPORT_ATTACHMENT_KINDS = ["screenshot", "upload"] as const;
export type SupportAttachmentKind = (typeof SUPPORT_ATTACHMENT_KINDS)[number];

export const supportAttachmentFileNameSchema = z
	.string()
	.trim()
	.min(1)
	.max(255);
